import React, { FC, useCallback, useEffect, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import Popup from 'reactjs-popup';
import { toast } from 'react-toastify';

import Button from '../../Parts/Button';
import apiCalls from '../../PowerShadesAPIFunctions';
import type { AwningOrderResponse } from '../awningTypes';

type AwningReleaseButtonProps = {
	quoteId: number;
	quoteType: string;
};

/**
 * Releases an awning order to KE. Awnings are built by KE, and Commerce owns the
 * transmission - this button just tells Commerce the order is cleared to go, and
 * Commerce transmits after its hold window.
 */
const AwningReleaseButton: FC<AwningReleaseButtonProps> = ({ quoteId, quoteType }) => {
	const [orderState, setOrderState] = useState<AwningOrderResponse | null>(null);
	const [statusLoading, setStatusLoading] = useState(true);
	const [statusError, setStatusError] = useState('');
	const [releasing, setReleasing] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);

	const refreshOrderState = useCallback(async () => {
		setStatusLoading(true);

		try {
			const resp = await apiCalls.getAwningOrder(quoteId);
			const data = resp.data ?? null;

			// The backend reports failures as HTTP 200 with an `error` key, so `error`
			// gets checked before anything else.
			if (data?.error) {
				setStatusError(data.error);
				setOrderState(null);
			} else if (!data) {
				setStatusError('Unable to reach the server.');
				setOrderState(null);
			} else {
				setStatusError('');
				setOrderState(data);
			}
		} catch (err) {
			console.error(err);
			setStatusError('Unable to load the awning order status.');
			setOrderState(null);
		}

		setStatusLoading(false);
	}, [quoteId]);

	useEffect(() => {
		refreshOrderState();
	}, [refreshOrderState]);

	const release = async () => {
		setReleasing(true);

		try {
			const resp = await apiCalls.releaseAwningOrder(quoteId);
			const data = resp.data ?? null;

			if (data?.error) {
				toast.error(data.error);
			} else if (!data) {
				toast.error('No response from the server. Please contact Engineering.');
			} else if (data.success) {
				const message = data.message ?? 'Awning order released to KE.';

				if (data.release_status === 'released') {
					let followUp = '';

					if (data.is_now_due) {
						followUp = ' It will go out on the next transmission pass.';
					} else if (data.hold_hours) {
						followUp = ` It will transmit to KE within ${data.hold_hours} hours.`;
					}

					toast.success(`${message}${followUp}`);
				} else {
					// already_released / already_transmitted are informational, not errors.
					toast.info(message);
				}
			} else {
				toast.error(data.message ?? 'Unable to release the awning order to KE.');
			}
		} catch (err) {
			console.error(err);
			toast.error('Unable to release the awning order to KE.');
		}

		setReleasing(false);
		setConfirmOpen(false);

		await refreshOrderState();
	};

	const order = orderState?.order ?? null;
	const canRelease = orderState?.can_release ?? false;

	let label = 'Release to KE';
	let tooltip = 'Release this awning order to KE';
	let disabled = true;

	if (statusLoading) {
		tooltip = 'Loading awning order status';
	} else if (statusError !== '') {
		label = 'Release to KE';
		tooltip = statusError;
	} else if (!orderState?.found) {
		label = 'Not in Commerce Yet';
		tooltip = 'Awning order not in Commerce yet';
	} else if (!canRelease) {
		if (order?.transmitted_at) {
			label = 'Transmitted to KE';
			tooltip = 'This awning order has already been transmitted to KE';
		} else {
			label = 'Already Released';
			tooltip = 'This awning order has already been released to KE';
		}
	} else {
		disabled = false;
	}

	return (
		<>
			<Col xl="auto" lg={3} md={3} sm={4} xs={6} className="mt-2 mt-xl-0">
				<Button
					fullWidth
					color={quoteType ?? 'green'}
					title={tooltip}
					disabled={disabled}
					loading={statusLoading || releasing}
					onClick={() => setConfirmOpen(true)}
				>
					{label}
				</Button>
			</Col>
			<Popup
				open={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				modal
				className="mobile-modal"
			>
				<Row>
					<Col>
						<Row>
							<Col>
								<h3>Release Awning Order to KE</h3>
							</Col>
						</Row>
						<Row>
							<Col>
								<p>
									This releases the awning order to KE for production. Commerce
									will transmit it after its hold window. Are you sure?
								</p>
							</Col>
						</Row>
						<div className="d-flex justify-content-between gap-3 pt-4">
							<Button color="gray" onClick={() => setConfirmOpen(false)}>
								Cancel
							</Button>
							<Button
								color={quoteType ?? 'green'}
								onClick={release}
								loading={releasing}
							>
								Release to KE
							</Button>
						</div>
					</Col>
				</Row>
			</Popup>
		</>
	);
};

export default AwningReleaseButton;
